import React from 'react'

import { FormattedMessage } from 'react-intl'
import { useLocation } from 'react-router'

import { RouterLink } from 'components/designSystem/Link/RouterLink'
import { FilterSvg } from 'components/designSystem/svgr/FilterSvg'
import { SettingsSvg } from 'components/designSystem/svgr/SettingsSvg'
import { photoRatingSettingsPath } from 'components/page/PhotoRatingSettings/PhotoRatingSettings.paths'
import { ROLLBACK_RATING_BUTTON } from 'components/page/RatingWave/Rating.constants'
import { searchSettingsPath } from 'components/page/Search/Search.paths'
import { pageServiceAlbums } from 'components/page/UserProfile/constants'
import { NameService } from 'components/page/UserProfile/UserProfileHeader/NameService'
import { profilePath, settingsModalPath } from 'components/paths'
import { ModalBackButton } from 'components/presentational/button/ModalBackButton'
import { mergeAllUrls } from 'functions/mergeAllUrls'
import { useAuthorized } from 'hooks/useAuthorized'
import { useCurrentUserRouteMatch } from 'hooks/useCurrentUserRouteMatch'
import { useLocale } from 'hooks/useLocale'
import { useNavigationSettings } from 'hooks/useNavigationSettings'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'
import { useUserId } from 'hooks/useUserId'

import { TemplateMenu } from './common/TemplateMenu'
import { EdnaCounterMessage } from './Element/EdnaCounterMessage'
import { Upload } from './Element/Upload'
import { HeaderLeftBlockPortal, HeaderRightBlockPortal } from './HeaderPortal'

export const PageSettings = () => {
  const { pathname } = useLocation()
  const userId = useUserId()
  const locale = useLocale()
  const authorized = useAuthorized()
  const { mobile } = useShallowEqualSelector(({ systemView: { mobile } }) => ({
    mobile,
  }))

  const {
    isRatingPath,
    isRatingSettingsPath,
    isRatingSettingsRequestGeoPermissionPath,
    isSearchPath,
    isSearchSettingsPath,
    isEventListPath,
    isChatPath,
    isProfilePath,
    isAlbumCurrentUserPath,
  } = useNavigationSettings()

  const isCurrentUserPath = useCurrentUserRouteMatch(pathname)

  /**
   * При уходе со страницы знакомств, мы сбрасываем все настройки для
   * того чтобы запросить заново при возвращение на знакомства.
   * Получаем свежие данные.
   */
  const localeUrl = !authorized ? locale : ''

  if (isRatingSettingsRequestGeoPermissionPath) {
    return <TemplateMenu backIcon={<HeaderLeftBlockPortal />} />
  }

  if (isRatingSettingsPath) {
    return (
      <TemplateMenu firstIcon={<HeaderRightBlockPortal />}>
        {mobile ? (
          <FormattedMessage id="search.filter.title" defaultMessage="Фильтры" />
        ) : (
          <FormattedMessage id="app.rating" defaultMessage="Знакомства" />
        )}
      </TemplateMenu>
    )
  }

  if (isRatingPath) {
    return (
      <TemplateMenu
        data-name="title-section-rating"
        firstIcon={
          <RouterLink
            to={mergeAllUrls(localeUrl, photoRatingSettingsPath)}
            data-name="rating-settings-action"
            black={true}
          >
            <FilterSvg width={24} height={24} />
          </RouterLink>
        }
        secondIcon={
          <div
            id={ROLLBACK_RATING_BUTTON}
            style={{
              width: '24px',
              height: '24px',
            }}
          ></div>
        }
      >
        <FormattedMessage id="app.rating" defaultMessage="Знакомства" />
      </TemplateMenu>
    )
  }

  if (isSearchSettingsPath) {
    return (
      <TemplateMenu
        data-name="title-section-search"
        firstIcon={<HeaderRightBlockPortal />}
      >
        {/** Такой дизайн ¯\_(ツ)_/¯ */}
        {mobile ? (
          <FormattedMessage id="search.filter.title" defaultMessage="Фильтры" />
        ) : (
          <FormattedMessage id="app.search" defaultMessage="Поиск" />
        )}
      </TemplateMenu>
    )
  }

  if (isSearchPath) {
    return (
      <TemplateMenu
        data-name="title-section-search"
        firstIcon={
          <RouterLink
            to={mergeAllUrls(localeUrl, searchSettingsPath)}
            data-name="search-settings-icon"
            black={true}
          >
            <FilterSvg width={24} height={24} />
          </RouterLink>
        }
      >
        <FormattedMessage id="app.search" defaultMessage="Поиск" />
      </TemplateMenu>
    )
  }

  if (isEventListPath) {
    return (
      <TemplateMenu data-name="title-section-event">
        <FormattedMessage
          id="popularity.statistic.hitlist"
          defaultMessage="События"
        />
      </TemplateMenu>
    )
  }

  if (isCurrentUserPath) {
    return (
      <TemplateMenu
        data-name="title-section-current-profile"
        firstIcon={
          <RouterLink
            to={mergeAllUrls(profilePath, userId, settingsModalPath)}
            data-name="link-main-settings-action"
            black={true}
          >
            <EdnaCounterMessage>
              <SettingsSvg width={24} height={24} />
            </EdnaCounterMessage>
          </RouterLink>
        }
      >
        <FormattedMessage id="app.profile.next" defaultMessage="Профиль" />
      </TemplateMenu>
    )
  }

  if (isChatPath) {
    return (
      <TemplateMenu data-name="title-section-chat">
        <FormattedMessage id="settings.messages" defaultMessage="Сообщения" />
      </TemplateMenu>
    )
  }

  if (isProfilePath) {
    return (
      <TemplateMenu
        data-name="title-section-foreign-profile"
        backIcon={<ModalBackButton />}
      />
    )
  }

  if (isAlbumCurrentUserPath) {
    return (
      <TemplateMenu
        data-name="title-section-album"
        backIcon={<ModalBackButton />}
        firstIcon={<Upload />}
      >
        <NameService serviceName={pageServiceAlbums} />
      </TemplateMenu>
    )
  }

  return <div></div>
}
